import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CanEdit } from "@/shared/readOnlyManager";
import axios from "axios";
import { useTable } from "react-table";
import { Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";

import { getGoalPlans } from "@/services/program/getGoalPlans";
import { getGoalPlan } from "@/services/program/getGoalPlan";
import { useDispatch, flashError, flashSuccess } from "@/shared/components/flash";
import ModalWrapper from "./ModalWrapper";

const STATUS_COPY = {
  Active: "active",
  Future: "scheduled",
  Expired: "expired",
};

const GoalPlans = ({ program, organization, status, reload = 0 }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [goalPlans, setGoalPlans] = useState([]);
  const [goalPlan, setGoalPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setOpen] = useState(false);
  const [modalName, setModalName] = useState(null);
  const [trigger, setTrigger] = useState(0);
  const [busyGoalPlanId, setBusyGoalPlanId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadGoalPlans = useCallback(async () => {
    if (!organization?.id || !program?.id) {
      setGoalPlans([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const items = await getGoalPlans(organization.id, program.id, status);
      setGoalPlans(Array.isArray(items) ? items : []);
    } catch (error) {
      setGoalPlans([]);
      setErrorMessage("Unable to load goal plans for this program.");
    } finally {
      setLoading(false);
    }
  }, [organization?.id, program?.id, status]);

  useEffect(() => {
    loadGoalPlans();
  }, [loadGoalPlans, reload, trigger]);

  useEffect(() => {
    const timeoutId = setTimeout(() => ReactTooltip.rebuild(), 0);
    return () => clearTimeout(timeoutId);
  }, [goalPlans, loading]);

  const toggle = useCallback((name = null, shouldReload = false) => {
    if (name) {
      setModalName(name);
      setOpen(true);
      return;
    }

    if (shouldReload) {
      setTrigger((value) => value + 1);
    }

    setOpen(false);
  }, []);

  const onClickEditGoalPlan = useCallback(
    async (goalPlanId) => {
      try {
        setBusyGoalPlanId(goalPlanId);
        const item = await getGoalPlan(organization.id, program.id, goalPlanId);
        setGoalPlan(item);
        toggle("EditGoalPlan");
      } catch (error) {
        flashError(dispatch, error?.response?.data || "Unable to load goal plan.");
      } finally {
        setBusyGoalPlanId(null);
      }
    },
    [dispatch, organization?.id, program?.id, toggle]
  );

  const onDeleteGoalPlan = useCallback(
    async (goalPlanId) => {
      try {
        setBusyGoalPlanId(goalPlanId);
        const response = await axios.delete(
          `/organization/${organization.id}/program/${program.id}/goalplan/${goalPlanId}`
        );

        if (response.status === 200) {
          flashSuccess(dispatch, "Goal plan deleted successfully!");
          setGoalPlans((currentGoalPlans) =>
            currentGoalPlans.filter((item) => String(item.id) !== String(goalPlanId))
          );
          if (String(goalPlan?.id) === String(goalPlanId)) {
            setOpen(false);
          }
        }
      } catch (error) {
        flashError(dispatch, error?.response?.data || "Unable to delete goal plan.");
      } finally {
        setBusyGoalPlanId(null);
      }
    },
    [dispatch, goalPlan?.id, organization?.id, program?.id]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Goal Plan",
        tooltip: "Name of the goal plan",
        accessor: "name",
        Cell: ({ value }) => (
          <div className="manager-program-settings__cell-stack">
            <strong>{value || "Untitled goal plan"}</strong>
          </div>
        ),
      },
      {
        Header: "Type",
        tooltip: "Type of goal plan",
        accessor: "goal_plan_type.name",
        Cell: ({ value }) => (
          <div className="manager-program-settings__cell-stack">
            <span>{value || "No goal plan type"}</span>
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: goalPlans,
  });

  const planDescriptor = STATUS_COPY[status] || "goal";
  const goalPlanCountLabel = `${goalPlans.length.toLocaleString()} ${planDescriptor} goal plan${
    goalPlans.length === 1 ? "" : "s"
  }`;

  return (
    <>
      <div className="manager-program-settings__table-meta">
        <span className="manager-program-settings__table-count">
          {loading ? t("loading") : goalPlanCountLabel}
        </span>

        {!loading && !errorMessage && goalPlans.length > 0 && (
          <span className="manager-program-settings__table-hint">
            Review goal-plan definitions and open a record to edit configuration.
          </span>
        )}
      </div>

      {loading && (
        <div className="manager-program-settings__empty">Loading goal plans...</div>
      )}

      {!loading && errorMessage && (
        <div className="manager-program-settings__empty">{errorMessage}</div>
      )}

      {!loading && !errorMessage && goalPlans.length === 0 && (
        <div className="manager-program-settings__empty">
          No {planDescriptor} goal plans are available for this program.
        </div>
      )}

      {!loading && !errorMessage && goalPlans.length > 0 && (
        <div className="manager-program-settings__table-shell">
          <Table borderless className="manager-program-settings__table table--stack-mobile" {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>
                      <span data-tip={column.tooltip} style={{ display: "inline-block" }}>
                        {column.render("Header")}
                      </span>
                    </th>
                  ))}
                  <CanEdit>
                    <th>Actions</th>
                  </CanEdit>
                </tr>
              ))}
            </thead>

            <tbody>
              {rows.map((row) => {
                prepareRow(row);
                const isBusy = busyGoalPlanId === row.original.id;

                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} data-label={typeof cell.column.Header === 'string' ? cell.column.Header : ''}>{cell.render("Cell")}</td>
                    ))}

                    <CanEdit>
                      <td data-label="Actions">
                        <div className="manager-program-settings__actions">
                          <button
                            type="button"
                            data-tip="Edit this goal plan's settings"
                            className="manager-program-settings__action-btn"
                            onClick={() => onClickEditGoalPlan(row.original.id)}
                            disabled={isBusy}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            data-tip="Permanently remove this goal plan"
                            className="manager-program-settings__action-btn is-danger"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this goal plan?"
                                )
                              ) {
                                onDeleteGoalPlan(row.original.id);
                              }
                            }}
                            disabled={isBusy}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </CanEdit>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <ModalWrapper
        name={modalName}
        isOpen={isOpen}
        setOpen={setOpen}
        toggle={toggle}
        goalplan={goalPlan}
        setGoalPlan={setGoalPlan}
      />
    </>
  );
};

export default GoalPlans;
